import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  arrayRemove,
  arrayUnion,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";

const ADMIN_EMAIL = "admin@gmail.com";

function normalizeEmail(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

export default function EmployeeAllowlistManager() {
  const { isAdmin } = useAuth();
  const [emails, setEmails] = useState([ADMIN_EMAIL]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const allowlistRef = useMemo(
    () => doc(db, "config", "employeeAllowlist"),
    []
  );

  useEffect(() => {
    if (!isAdmin) return;

    const unsub = onSnapshot(
      allowlistRef,
      (snap) => {
        setError(null);
        if (!snap.exists()) {
          setEmails([ADMIN_EMAIL]);
          setLoading(false);
          return;
        }

        const data = snap.data();
        const list = Array.isArray(data.emails) ? data.emails : [];
        const normalized = list.map(normalizeEmail).filter(Boolean);
        const merged = Array.from(new Set([ADMIN_EMAIL, ...normalized]));
        setEmails(merged);
        setLoading(false);
      },
      (e) => {
        setError(e?.message || "Failed to load allowlist.");
        setLoading(false);
      }
    );

    return () => unsub();
  }, [allowlistRef, isAdmin]);

  const initialize = async () => {
    setError(null);
    try {
      await setDoc(
        allowlistRef,
        { emails: [ADMIN_EMAIL], updatedAt: serverTimestamp() },
        { merge: true }
      );
    } catch (e) {
      setError(e?.message || "Failed to initialize allowlist.");
    }
  };

  const addEmail = async () => {
    const email = normalizeEmail(input);
    if (!email || !email.includes("@")) return;

    setError(null);
    try {
      await updateDoc(allowlistRef, {
        emails: arrayUnion(email),
        updatedAt: serverTimestamp(),
      });
      setInput("");
    } catch (e) {
      // If doc doesn't exist yet, create it.
      try {
        await setDoc(
          allowlistRef,
          { emails: [ADMIN_EMAIL, email], updatedAt: serverTimestamp() },
          { merge: true }
        );
        setInput("");
      } catch (e2) {
        setError(e2?.message || "Failed to add email.");
      }
    }
  };

  const removeEmail = async (email) => {
    const normalized = normalizeEmail(email);
    if (!normalized || normalized === ADMIN_EMAIL) return;

    setError(null);
    try {
      await updateDoc(allowlistRef, {
        emails: arrayRemove(normalized),
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      setError(e?.message || "Failed to remove email.");
    }
  };

  if (!isAdmin) return null;

  return (
    <Card>
      <CardTitle>Employee Access (Email Allowlist)</CardTitle>
      <CardSub>Only emails in this list can access the Work Tracker.</CardSub>

      {error && <ErrorText>{error}</ErrorText>}

      <Row>
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Add employee email…"
        />
        <PrimaryButton onClick={addEmail} disabled={!input.trim()}>
          Add
        </PrimaryButton>
        <GhostButton onClick={initialize} disabled={loading}>
          Initialize
        </GhostButton>
      </Row>

      <List>
        {emails.map((e) => (
          <Item key={e}>
            <Email>{e}</Email>
            <RemoveButton
              onClick={() => removeEmail(e)}
              disabled={e === ADMIN_EMAIL}
              title={
                e === ADMIN_EMAIL ? "Admin email can't be removed" : "Remove"
              }
            >
              Remove
            </RemoveButton>
          </Item>
        ))}
      </List>
    </Card>
  );
}

const Card = styled.div`
  background: var(--secondaryBackgroundColor);
  padding: 1.5em;
  border-radius: 14px;
  color: var(--fontPrimaryColor);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  margin-bottom: 1.5em;
`;

const CardTitle = styled.h2`
  margin: 0 0 6px 0;
  font-size: 1.1rem;
`;

const CardSub = styled.p`
  margin: 0 0 12px 0;
  color: var(--fontSecondaryColor);
  font-size: 0.95rem;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const Input = styled.input`
  flex: 1;
  min-width: 240px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: var(--thirdBackgroundColor);
  color: var(--fontPrimaryColor);
  outline: none;
`;

const Button = styled.button`
  border: none;
  border-radius: 10px;
  padding: 10px 14px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background: var(--primaryColor);
  color: white;
`;

const GhostButton = styled(Button)`
  background: transparent;
  color: var(--fontPrimaryColor);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Item = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.02);
  padding: 10px 12px;
  border-radius: 12px;
`;

const Email = styled.div`
  font-weight: 600;
`;

const RemoveButton = styled(Button)`
  background: rgba(239, 68, 68, 0.2);
  color: rgba(239, 68, 68, 0.95);
`;

const ErrorText = styled.div`
  margin: 6px 0 12px 0;
  color: rgba(239, 68, 68, 0.95);
  font-weight: 600;
`;
